/**
 * Structured entity data types matching backend DTOs.
 * Replaces regex-based markdown parsing with type-safe data consumption.
 *
 * @see https://github.com/telegraphic-dev/brick-directory/issues/435
 */

/**
 * Structured data for a LEGO set entity.
 */
export interface SetData {
  setNum: string;
  name: string;
  year?: number;
  theme?: string;
  totalParts: number;
  totalMinifigures: number;
  totalSubSets: number;
  imageUrl?: string;
  bricklinkId?: string;
  bricklinkName?: string;
  minifigures?: MinifigEntry[];
  parts?: PartEntry[];
  subSets?: SubSetEntry[];
}

/**
 * Structured data for a LEGO part entity.
 */
export interface PartData {
  partNum: string;
  name: string;
  category?: string;
  material?: string;
  imageUrl?: string;
  bricklinkId?: string;
  bricklinkName?: string;
  totalUsages?: number;
  uniqueColors?: number;
  totalQuantity?: number;
  colors?: ColorEntry[];
}

/**
 * Structured data for a LEGO minifigure entity.
 */
export interface MinifigData {
  figNum: string;
  name: string;
  numParts?: number;
  imageUrl?: string;
  bricklinkId?: string;
  bricklinkName?: string;
  sets?: SetEntry[];
  parts?: PartEntry[];
}

/**
 * Structured data for a LEGO element entity.
 */
export interface ElementData {
  elementId: string;
  partNum: string;
  partName: string;
  colorName: string;
  colorHex?: string;
  isTransparent?: boolean;
  category?: string;
  material?: string;
  imageUrl?: string;
  sets?: SetEntry[];
  minifigures?: MinifigEntry[];
}

/**
 * A minifig entry within a set or element.
 */
export interface MinifigEntry {
  figNum: string;
  name: string;
  imageUrl?: string;
  quantity: number;
}

/**
 * A part entry within a set or minifig.
 */
export interface PartEntry {
  partNum: string;
  elementId?: string;
  name: string;
  colorName?: string;
  colorHex?: string;
  imageUrl?: string;
  quantity: number;
}

/**
 * A set entry within a minifig or element.
 */
export interface SetEntry {
  setNum: string;
  name?: string;
  year?: number;
  imageUrl?: string;
  quantity: number;
}

/**
 * A sub-set entry within a set.
 */
export interface SubSetEntry {
  setNum: string;
  name?: string;
  quantity: number;
}

/**
 * A color entry for part color availability.
 */
export interface ColorEntry {
  elementId?: string;
  colorName: string;
  colorHex?: string;
  imageUrl?: string;
  setsUsing: number;
}

/**
 * A structured suggestion link (external link or chat suggestion).
 */
export interface SuggestionLink {
  text: string;
  url?: string;
  emoji?: string;
  type: 'chat' | 'external';
}

/**
 * Union type for all structured entity data.
 */
export type EntityData = SetData | PartData | MinifigData | ElementData;

/**
 * API response from /api/entities/{type}/{id}
 */
export interface EntityDetailsResponse {
  /** @deprecated Prefer structuredData for rendering */
  summary: string | null;
  /** @deprecated Prefer structuredData for rendering */
  content: string | null;
  /** @deprecated Prefer structuredSuggestions for typed handling */
  suggestions: string[];
  /** Structured entity data for type-safe rendering */
  structuredData?: EntityData;
  /** Typed suggestion links */
  structuredSuggestions?: SuggestionLink[];
}

/**
 * Type guard: check if structured data is SetData.
 */
export function isSetData(data: EntityData): data is SetData {
  return 'setNum' in data && 'totalParts' in data;
}

/**
 * Type guard: check if structured data is PartData.
 */
export function isPartData(data: EntityData): data is PartData {
  return 'partNum' in data && !('colorName' in data) && !('figNum' in data);
}

/**
 * Type guard: check if structured data is MinifigData.
 */
export function isMinifigData(data: EntityData): data is MinifigData {
  return 'figNum' in data;
}

/**
 * Type guard: check if structured data is ElementData.
 */
export function isElementData(data: EntityData): data is ElementData {
  return 'elementId' in data && 'partNum' in data;
}
